import RequestService, {
    RequestServiceResponse,
} from '@tcc-online/request-service';

import { StorageService } from '../';
import { AuthService } from '../AuthService';
import { IAuthUser } from '../../types';
import { NotificationActions } from '../../common/actions/NotificationActions';
import { GetThunkAPI } from '@reduxjs/toolkit/dist/createAsyncThunk';
import * as Sentry from '@sentry/react';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

export const UserService = {
    authUser,
    refreshAccess,
    setCustomer,
    logout,
    isLogged,
    hasExpired,
    isAdmin,
    getAccessToken,
    getRefreshToken,
    getUserId,
    getActiveQuestionnaires,
    resetPassword,
    requestResetPassword,
    changePassword,
    getAllowedLanguages,
    authUserWithToken,
    isAnonymous,
};

async function authUser(user: IAuthUser) {
    return await RequestService.post('/auth/login', user, {
        ignoreAuth: true,
    });
}

async function refreshAccess() {
    const refreshToken = getRefreshToken();
    return await RequestService.post(
        '/auth/refresh',
        {
            refreshToken,
        },
        { ignoreAuth: true },
    );
}

function setCustomer(accessToken: string, refreshToken: string) {
    StorageService.setLocal(ACCESS_TOKEN_KEY, accessToken);
    StorageService.setLocal(REFRESH_TOKEN_KEY, refreshToken);

    const data = AuthService.decodeUserData(accessToken);
    if (data) {
        Sentry.setUser({ id: data.sub, companyId: data.companyId });
    }
}

function logout() {
    StorageService.deleteLocal(ACCESS_TOKEN_KEY);
    StorageService.deleteLocal(REFRESH_TOKEN_KEY);

    Sentry.setUser(null);
}

function isLogged(): boolean {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    return !!accessToken && !!refreshToken;
}

function hasExpired(): boolean {
    const accessToken = getAccessToken();
    return !!accessToken && AuthService.isAccessTokenExpired(accessToken);
}

function isAdmin(): boolean {
    const accessToken = getAccessToken();
    return AuthService.isUserAdmin(accessToken);
}

function getUserId(): number {
    const accessToken = getAccessToken();
    return AuthService.getKey(accessToken, 'sub');
}

function getAccessToken(): string {
    return StorageService.getLocal<string>(ACCESS_TOKEN_KEY);
}

function getRefreshToken(): string {
    return StorageService.getLocal<string>(REFRESH_TOKEN_KEY);
}

function getAllowedLanguages(): string[] {
    const accessToken = getAccessToken();
    return AuthService.getKey(accessToken, 'allowedLanguages');
}

async function getActiveQuestionnaires() {
    return await RequestService.get('/participant/active-questionnaires');
}

async function requestResetPassword(email: string) {
    return await RequestService.post(
        '/request-reset-password',
        {
            email,
        },
        { ignoreAuth: true },
    );
}

async function resetPassword(payload: {
    controlPassword: string;
    password: string;
}) {
    const { controlPassword, password } = payload;

    return await RequestService.post(
        '/reset-password',
        {
            controlPassword,
            password,
        },
        { ignoreAuth: true },
    );
}

type ChangePasswordPayload = {
    controlPassword: string;
    password: string;
    userId: number;
};

async function changePassword(
    payload: ChangePasswordPayload,
    api: GetThunkAPI<any>,
): Promise<
    RequestServiceResponse<any, { controlPassword: string; password: string }>
> {
    const { controlPassword, password, userId } = payload;

    try {
        const response = await RequestService.post(
            `/users/${userId}/change-password`,
            {
                controlPassword,
                password,
            },
        );

        if (response?.status === 204) {
            api.dispatch(
                NotificationActions.success('fe.change-password.success'),
            );
        }

        return response;
    } catch (ex) {
        throw new Error(ex.response?.statusText || ex.message);
    }
}

async function authUserWithToken(payload: { token: string; params: string }) {
    const { token, params } = payload;
    return await RequestService.post(
        '/auth/login',
        { token, params: params ? JSON.parse(params) : null },
        { ignoreAuth: true },
    );
}

function isAnonymous() {
    const accessToken = getAccessToken();
    return AuthService.isAnonymous(accessToken);
}
